/**
 * Pending-apply memory: "you opened this application, did you apply?" asked
 * on the NEXT visit, not only on a tab return inside the same session.
 *
 * THE PROBLEM (seeker funnel RCA, 2026-10-14). `ConfirmApplyToast` only asks
 * when the visitor returns to the tab >= 3 min after the outbound hop AND the
 * 2h `ld_pending_hop` cookie is still alive. A seeker who applies tonight and
 * comes back tomorrow is never asked. Prompts shown fell 202 -> 61 per week
 * while outbound apply clicks stayed flat.
 *
 * WHY `localStorage`. The memory must outlive the session and the 2h cookie,
 * and it must never become a `Set-Cookie` on a cacheable surface. Nothing here
 * is identity: a job id, optional public job fields, and timestamps.
 *
 * FAIL-OPEN. Every access is wrapped. A storage error means "nothing pending",
 * which is the behaviour that existed before this module.
 */

import { internalJobId } from "@/lib/jobs/public-id";

const STORAGE_KEY = "ld_pending_applies_v1";

/** How long an outbound apply click stays askable. */
export const PENDING_APPLY_TTL_MS = 7 * 24 * 60 * 60 * 1000;
/** Most recent clicks kept; older ones are dropped first. */
export const PENDING_APPLY_CAP = 10;

export type PendingApply = {
	jobId: string;
	company: string | null;
	title: string | null;
	/** Epoch ms of the outbound apply click. */
	clickedAt: number;
	/** Epoch ms the next-visit prompt was shown. Shown once per click. */
	askedAt?: number;
	/** The server's `ld_pending_hop` cookie was seen for this job, i.e. the
	 * real outbound hop happened with the confirm loop switched on. */
	hopSeen?: boolean;
};

function isEntry(value: unknown): value is PendingApply {
	if (!value || typeof value !== "object") return false;
	const v = value as Record<string, unknown>;
	return (
		typeof v.jobId === "string" &&
		v.jobId.length > 0 &&
		typeof v.clickedAt === "number" &&
		Number.isFinite(v.clickedAt)
	);
}

function normalise(v: PendingApply): PendingApply {
	return {
		jobId: v.jobId,
		company: typeof v.company === "string" && v.company ? v.company : null,
		title: typeof v.title === "string" && v.title ? v.title : null,
		clickedAt: v.clickedAt,
		...(typeof v.askedAt === "number" ? { askedAt: v.askedAt } : {}),
		...(v.hopSeen === true ? { hopSeen: true } : {}),
	};
}

/** Live entries, newest click first. Expired and malformed entries are dropped. */
export function readPendingApplies(now: number = Date.now()): PendingApply[] {
	let raw: string | null = null;
	try {
		raw = window.localStorage.getItem(STORAGE_KEY);
	} catch {
		return [];
	}
	if (!raw) return [];
	let parsed: unknown;
	try {
		parsed = JSON.parse(raw);
	} catch {
		return [];
	}
	if (!Array.isArray(parsed)) return [];
	return parsed
		.filter(isEntry)
		.map(normalise)
		.filter((e) => now - e.clickedAt < PENDING_APPLY_TTL_MS)
		.sort((a, b) => b.clickedAt - a.clickedAt)
		.slice(0, PENDING_APPLY_CAP);
}

function write(entries: PendingApply[]): void {
	try {
		if (entries.length === 0) {
			window.localStorage.removeItem(STORAGE_KEY);
			return;
		}
		window.localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
	} catch {
		// Quota / blocked storage: the next-visit ask simply does not happen.
	}
}

/**
 * Remember an outbound apply click. A repeat click on the same job is a NEW
 * apply attempt: it moves to the front and becomes askable again. Known
 * company/title are kept when the new record carries none (the anchor
 * listener knows only the job id; the job-page CTA knows both).
 */
export function recordPendingApply(
	entry: {
		jobId: string;
		company?: string | null;
		title?: string | null;
		clickedAt?: number;
	},
	now: number = Date.now(),
): void {
	if (!entry.jobId) return;
	const existing = readPendingApplies(now);
	const prior = existing.find((e) => e.jobId === entry.jobId);
	const clickedAt = entry.clickedAt ?? now;
	// The anchor listener and the CTA both fire for one click; a second record
	// inside a few seconds is the same click, so it must not wipe `hopSeen`.
	const sameClick = prior !== undefined && clickedAt - prior.clickedAt < 10_000;
	const next: PendingApply = {
		jobId: entry.jobId,
		company: entry.company || prior?.company || null,
		title: entry.title || prior?.title || null,
		clickedAt: sameClick ? prior.clickedAt : clickedAt,
		...(sameClick && prior?.hopSeen ? { hopSeen: true } : {}),
	};
	write(
		[next, ...existing.filter((e) => e.jobId !== entry.jobId)].slice(
			0,
			PENDING_APPLY_CAP,
		),
	);
}

/** Merge fields into one entry (no-op when the job is not pending). */
export function updatePendingApply(
	jobId: string,
	patch: Partial<Omit<PendingApply, "jobId" | "clickedAt">>,
	now: number = Date.now(),
): void {
	const existing = readPendingApplies(now);
	if (!existing.some((e) => e.jobId === jobId)) return;
	write(
		existing.map((e) =>
			e.jobId === jobId ? normalise({ ...e, ...patch }) : e,
		),
	);
}

/** Forget a job: it was answered (on any surface) or dismissed. */
export function removePendingApply(
	jobId: string,
	now: number = Date.now(),
): void {
	if (!jobId) return;
	const existing = readPendingApplies(now);
	if (!existing.some((e) => e.jobId === jobId)) return;
	write(existing.filter((e) => e.jobId !== jobId));
}

const OUT_PATH = /^\/(?:id|en)\/out\/([^/?#]+)/;

/** The internal job id of an outbound `/<locale>/out/<id>/...` link, or null. */
export function outboundJobIdFromHref(
	href: string,
	base: string,
): string | null {
	let pathname: string;
	try {
		pathname = new URL(href, base).pathname;
	} catch {
		return null;
	}
	const m = pathname.match(OUT_PATH);
	if (!m?.[1]) return null;
	let id: string;
	try {
		id = decodeURIComponent(m[1]);
	} catch {
		return null;
	}
	return internalJobId(id);
}

/** Calendar days between the click and now, in the visitor's local time. */
export function daysSinceClick(clickedAt: number, now: number): number {
	const start = (t: number) => {
		const d = new Date(t);
		d.setHours(0, 0, 0, 0);
		return d.getTime();
	};
	return Math.max(0, Math.round((start(now) - start(clickedAt)) / 86_400_000));
}

/**
 * The one entry to ask about on this page view, or null.
 *
 * - `clickedAt < pageLoadAt`: a click made in THIS page view belongs to the
 *   same-session tab-return path, never to this one.
 * - `minDelayMs`: same "give them time to apply" floor as the tab-return path.
 * - `maxAgeMs`: how far back an answer can still be recorded (a guest's
 *   answer needs the 24h `hunt_anon_sid` cookie the hop set).
 * - `skip(jobId)`: owned by another surface (cookie path, in-page ask, or
 *   already answered this session).
 */
export function pickNextVisitCandidate(
	entries: PendingApply[],
	opts: {
		now: number;
		pageLoadAt: number;
		minDelayMs: number;
		maxAgeMs: number;
		skip: (entry: PendingApply) => boolean;
	},
): PendingApply | null {
	for (const e of entries) {
		if (e.askedAt !== undefined) continue;
		if (e.clickedAt >= opts.pageLoadAt) continue;
		const age = opts.now - e.clickedAt;
		if (age < opts.minDelayMs || age > opts.maxAgeMs) continue;
		if (opts.skip(e)) continue;
		return e;
	}
	return null;
}

/** Exported for tests. */
export function clearPendingApplies(): void {
	try {
		window.localStorage.removeItem(STORAGE_KEY);
	} catch {
		// ignore
	}
}
